import type { Chord } from '@tunasong/music-lib'
import { ChordLib, GuitarLib, isChordVariant, NoteLib, ScaleLib } from '@tunasong/music-lib'
import type { Scale } from '@tunasong/schemas'
import { useDesignTokens } from '@tunasong/ui-lib'
import type { FC } from 'react'
import React, { useEffect, useRef, useState } from 'react'
import { defaultHeight, defaultWidth, draw } from './guitar-chord-draw.js'

const containerStyle: React.CSSProperties = { display: 'inline-flex' }

export interface ChordProps {
  className?: string
  chord?: Chord
  /** If specified, the in/out scale notes will be marked */
  scale?: Scale | null
}

const canvasStyle: React.CSSProperties = {
  flex: 1,
  width: defaultWidth,
  height: defaultHeight,
}

/**
 * Layout
 *
 *  Chord name
 *  --padding--
 *  x   o    x
 *  ============ (nut)
 *    o          (fingering)
 *  ------------ (fret)
 *        o      (fingering)
 *  ------------ (fret)
 *
 *    2   3       (fingering fret number)
 */

export const GuitarChord: FC<ChordProps> = props => {
  const { className, chord, scale } = props

  const tokens = useDesignTokens()
  const canvasRef = React.useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(false)

  /** Defer canvas drawing until visible in viewport */
  useEffect(() => {
    const el = containerRef.current
    if (!el) {
      return
    }
    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true)
      return
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { rootMargin: '200px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const type = isChordVariant(chord) ? (chord?.variant ?? '') : ''

  useEffect(() => {
    if (!(isVisible && canvasRef.current && chord)) {
      return
    }

    const scaleDegree = scale && isChordVariant(chord) ? ScaleLib.getChordDegree(scale, chord) : undefined
    const name = ChordLib.getName(chord)
    const rawFingering = isChordVariant(chord)
      ? GuitarLib.getFingering(chord)
      : GuitarLib.isGuitarChord(chord)
        ? chord.fingering
        : null
    const fingering = GuitarLib.getFingeringWithMetadata(rawFingering ?? [], scale)
    const label = name ? NoteLib.prettifyNote(name) : 'Custom'

    draw({ canvas: canvasRef.current, fingering, degree: scaleDegree, label, type, tokens })
  }, [chord, isVisible, scale, tokens, type])

  return (
    <div ref={containerRef} style={containerStyle}>
      <canvas style={canvasStyle} className={className} ref={canvasRef} />
    </div>
  )
}

export default GuitarChord
